import {
  KentRebalancingPlanStatusEnum,
  NormalizedRebalancingPlan,
  RebalancingPlanStatusEnum,
} from '@cast/types';
import { notReachable } from '@cast/utils';

import {
  EXECUTED_REBALANCING_STATES,
  KENT_EXECUTED_REBALANCING_STATES,
  KENT_REBALANCING_IN_PROGRESS_STATES,
  REBALANCING_IN_PROGRESS_STATES,
} from 'constants/cluster';

export const isNormalizedRebalancingPlanInProgress = (
  plan: NormalizedRebalancingPlan
): boolean => {
  switch (plan.variant) {
    case 'autoscaler':
      return REBALANCING_IN_PROGRESS_STATES.some(
        (status) => status === plan.rawPlan.status
      );
    case 'kent':
      return KENT_REBALANCING_IN_PROGRESS_STATES.some(
        (status) => status === plan.rawPlan.status
      );
    default:
      return notReachable(plan);
  }
};

export const isNormalizedRebalancingPlanError = (
  plan: NormalizedRebalancingPlan
): boolean => {
  switch (plan.variant) {
    case 'autoscaler':
      return plan.rawPlan.status === RebalancingPlanStatusEnum.error;
    case 'kent':
      return plan.rawPlan.status === KentRebalancingPlanStatusEnum.FAILED;
    default:
      return notReachable(plan);
  }
};

export const isNormalizedRebalancingPlanFinished = (
  plan: NormalizedRebalancingPlan
): boolean => {
  switch (plan.variant) {
    case 'autoscaler':
      return plan.rawPlan.status === RebalancingPlanStatusEnum.finished;
    case 'kent':
      return plan.rawPlan.status === KentRebalancingPlanStatusEnum.COMPLETED;
    default:
      return notReachable(plan);
  }
};

export const isNormalizedRebalancingPlanFinishedOrPartiallyFinished = (
  plan: NormalizedRebalancingPlan
): boolean => {
  switch (plan.variant) {
    case 'autoscaler':
      return (
        plan.rawPlan.status === RebalancingPlanStatusEnum.finished ||
        plan.rawPlan.status === RebalancingPlanStatusEnum.partially_finished
      );
    case 'kent':
      return (
        plan.rawPlan.status === KentRebalancingPlanStatusEnum.COMPLETED ||
        plan.rawPlan.status ===
          KentRebalancingPlanStatusEnum.PARTIALLY_COMPLETED
      );
    default:
      return notReachable(plan);
  }
};

export const isNormalizedRebalancingPlanInGeneratedState = (
  plan: NormalizedRebalancingPlan
): boolean => {
  switch (plan.variant) {
    case 'autoscaler':
      return plan.rawPlan.status === RebalancingPlanStatusEnum.generated;
    case 'kent':
      return plan.rawPlan.status === KentRebalancingPlanStatusEnum.READY;
    default:
      return notReachable(plan);
  }
};

export const isNormalizedRebalancingPlanGenerating = (
  plan: NormalizedRebalancingPlan
): boolean => {
  switch (plan.variant) {
    case 'autoscaler':
      return plan.rawPlan.status === RebalancingPlanStatusEnum.generating;
    case 'kent':
      return plan.rawPlan.status === KentRebalancingPlanStatusEnum.GENERATING;
    default:
      return notReachable(plan);
  }
};

export const isNormalizedRebalancingPlanGeneratedOrGenerating = (
  plan: NormalizedRebalancingPlan
): boolean =>
  isNormalizedRebalancingPlanInGeneratedState(plan) ||
  isNormalizedRebalancingPlanGenerating(plan);

export const isNormalizedRebalancingPlanExecuted = (
  plan: NormalizedRebalancingPlan
): boolean => {
  switch (plan.variant) {
    case 'autoscaler':
      return EXECUTED_REBALANCING_STATES.some(
        (status) => status === plan.rawPlan.status
      );
    case 'kent':
      return KENT_EXECUTED_REBALANCING_STATES.some(
        (status) => status === plan.rawPlan.status
      );
    default:
      return notReachable(plan);
  }
};

export const isNormalizedRebalancingPlanSkipped = (
  plan: NormalizedRebalancingPlan
): boolean => {
  switch (plan.variant) {
    case 'autoscaler':
      // autoscaler does not have a skipped status
      return false;
    case 'kent':
      return plan.rawPlan.status === KentRebalancingPlanStatusEnum.SKIPPED;
    default:
      return notReachable(plan);
  }
};
